import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Pagination, Popover, Table, Whisper } from "rsuite";
import toaster from "rsuite/toaster";
import { ModalData, ModalPaymentInformation } from "../../components/modal";
import { PaginationEntriesInput } from "../../components/paginationEntries";
import { SendMail } from "../../components/sendMail.component";
import { DateCell, TextCell } from "../../components/tableItem.component";
import { InvoiceModal } from "../../components/viewInvoice.component";
import { ApiInvoice } from "../../core/apis/invoice";
import { useAppDispatch, useAppSelector } from "../../core/redux";
import { ActionsFinanceCaregiverTable } from "../../core/redux/slices/sliceFinanceTable";
import { ActionsModal } from "../../core/redux/slices/sliceModal";
import { EnumModals } from "../../libs";
import {
  MessageDownloadError,
  MessageDownloaded,
  MessageDownloading,
} from "../../libs/exportDownload";
import { ApiSchemaFinanceCaregiver } from "../../models/api/finance";
import { sortTableData } from "../../utils/tableFilter";
import { ApiSettings } from "../../core/apis";
import { convertToTwoDecimal } from "../../utils/convertToTwoDecimal";
import { UtilityDate } from "../../utils/dateUtils";
import { FaCircleDown } from 'react-icons/fa6';
import { FiDownload } from "react-icons/fi";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaFinanceCaregiver & {
  checked?: boolean;
  name?: string;
};

type TypeTableKey = keyof TableData;

type CaregiverListProps = {
  isValidating?: boolean;
  data?: ApiSchemaFinanceCaregiver[];
  totalCount: number;
};

const BadgeCell = ({ rowData, ...props }: { rowData?: TableData }) => {
  const getColor = (status?: string) => {
    return status === "Paid"
      ? "#00BDB4"
      : status === "Due"
      ? "#FF0000"
      : "#808080";
  };

  return (
    <Cell {...props}>
      <Badge
        content={rowData?.caregiverPaymentStatus || "Paid"}
        style={{ background: getColor(rowData?.caregiverPaymentStatus) }}
        className="p-1 px-2"
      />
    </Cell>
  );
};

export const CaregiverFinanceList = ({
  data,
  isValidating,
  totalCount,
}: CaregiverListProps) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const { pageNumber, pageSize, isLoading } = useAppSelector(
    (state) => state.financeCaregiverTable
  );
  const dispatch = useAppDispatch();
  const [sortBy, setSortBy] = useState<TypeTableKey>("" as TypeTableKey);
  const [sortAsc, setSortAsc] = useState(true);
  const [currentInvoiceKey, setCurrentInvoiceKey] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [haveEmail, setHaveEmail] = useState(false);
  const [isLoadingEmailData, setIsLoadingEmailData] = useState(false);

  const getEmailData = useCallback(async () => {
    setIsLoadingEmailData(true);
    const response = await ApiSettings.GetSenderEmail();
    if (response.isSuccess && response.data?.senderEmail) {
      setHaveEmail(true);
    }
    setIsLoadingEmailData(false);
  }, []);

  useEffect(
    () =>
      setTableData(
        data?.map((caregiver) => {
          const {caregiverTotalAmount, ...rest} = caregiver
          return ({
          name:
            caregiver.caregiverFirstName + " " + caregiver.caregiverLastName,
          confirmedBy: caregiver.confirmedByUserFirstName
            ? caregiver.confirmedByUserFirstName +
              " " +
              caregiver.confirmedByUserLastName
            : "",
            caregiverTotalAmount: convertToTwoDecimal(caregiverTotalAmount as number),
          ...rest,
        })}) ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const filteredTableData = useMemo(() => {
    return sortTableData(tableData, sortBy, sortAsc);
  }, [sortAsc, sortBy, tableData]);

  const MarkAsPaidCell = ({
    rowData,
    ...props
  }: {
    rowData?: ApiSchemaFinanceCaregiver;
  }) => {
    return (
      <Cell {...props}>
        {rowData?.caregiverPaymentStatus === "Paid" ? (
          ""
        ) : (
          <p
            className="text-green-200 border d-inline px-2 rounded-pill border-green-200 click"
            onClick={() => {
              setCurrentInvoiceKey(rowData?.invoiceSKey || "");
              setShowPaymentModal(true);
            }}
          >
            Mark as paid
          </p>
        )}
      </Cell>
    );
  };

  const handleDownload = async (
    invoiceId?: string,
    selectedYearMonth?: Date,
    isPatientReport?: boolean
  ) => {
    const startId = toaster.push(<MessageDownloading />, {
      placement: "topEnd",
    });
    try {
      await ApiInvoice.DownloadExcelFinance(
        invoiceId,
        selectedYearMonth?.toISOString(),
        isPatientReport
      );
      toaster.push(<MessageDownloaded />, {
        placement: "topEnd",
      });
    } catch {
      toaster.push(<MessageDownloadError />, {
        placement: "topEnd",
      });
    } finally {
      toaster.remove(startId);
    }
  };

  const handleDownloadFinance = (rowData: TableData) => {
    ModalData.InvoiceDownloadModal.startDate = rowData.dateFrom;
    ModalData.InvoiceDownloadModal.endDate = rowData.dateTo;
    ModalData.InvoiceDownloadModal.OkButtonText = "Done";
    ModalData.InvoiceDownloadModal.handleDownload = (
      selectedYearMonth: Date
    ) => {
      dispatch(ActionsModal.hideModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
      handleDownload(rowData.invoiceSKey, selectedYearMonth, false);
    };
    dispatch(ActionsModal.openModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
  };

  useEffect(() => {
    getEmailData();
  }, [getEmailData]);

  return (
    <div>
      <ModalPaymentInformation
        show={showPaymentModal}
        setShow={setShowPaymentModal}
        invoiceSKey={currentInvoiceKey}
        paymentFor="caregiver"
        Action={ActionsFinanceCaregiverTable}
      />
      <div className="p-2 d-flex align-item-center">
        <div className="align-self-center me-2">Show</div>
        <PaginationEntriesInput
          onChange={(value) =>
            dispatch(
              ActionsFinanceCaregiverTable.setEntryCount(value as number)
            )
          }
        />
        <div className="align-self-center ms-2">entries</div>
      </div>

      <Table
        autoHeight
        rowClassName="table-row-hover"
        bordered
        cellBordered
        hover
        classPrefix="rs-table"
        className="table-striped"
        data={filteredTableData}
        loading={isValidating || isLoading || isLoadingEmailData}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Data Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
      >
        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Caregiver">Caregiver </span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="name" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="From">From</span>
          </HeaderCell>
          <Cell dataKey="dateFrom">
            {(rowData: ApiSchemaFinanceCaregiver) =>
              rowData.dateFrom ? UtilityDate.formatHumanFriendlyDate(new Date(rowData.dateFrom)) : ''
            }
          </Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="To">To</span>
          </HeaderCell>
          <Cell dataKey="dateTo">
            {(rowData: ApiSchemaFinanceCaregiver) =>
              rowData.dateTo ? UtilityDate.formatHumanFriendlyDate(new Date(rowData.dateTo)) : ''
            }
          </Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Total Amount">Total Amount</span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="caregiverTotalAmount" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Check Number</HeaderCell>
          <TextCell dataKey="checkNumber" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Payment Date</HeaderCell>
          <Cell dataKey="paymentDate">
            {(rowData: ApiSchemaFinanceCaregiver) =>
              rowData.paymentDate && rowData.caregiverPaymentStatus
                ? UtilityDate.formatHumanFriendlyDate(new Date(rowData.paymentDate))
                : ""
            }
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Payment Note</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaFinanceCaregiver) =>
              rowData.paymentNote ? (
                <Whisper
                  placement="topStart"
                  speaker={<Popover>{rowData.paymentNote}</Popover>}
                >
                  <span>{rowData.paymentNote}</span>
                </Whisper>
              ) : (
                ""
              )
            }
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Payment Confirmed By">Payment Confirmed By</span>
          </HeaderCell>
          <TextCell component="Caregiver" dataKey="confirmedBy" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Status">Status</span>
          </HeaderCell>
          <BadgeCell />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Stage</HeaderCell>
          <MarkAsPaidCell />
        </Column>

        <Column align="center" flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
                <div className="d-flex align-items-center justify-content-between gap-2">
                <InvoiceModal
                  invoiceId={rowData.invoiceSKey!}
                  resetKey={"123"}
                  invoiceStartDate={rowData.dateFrom}
                  invoiceEndDate={rowData.dateTo}
                  pageTitle="caregiver-finance"
                />
                <SendMail
                  alreadySent={false}
                  value={rowData}
                  fromTab="caregiverFinance"
                  invoiceId={rowData.invoiceSKey!}
                  invoiceEndDate={rowData.dateTo}
                  invoiceStartDate={rowData.dateFrom}
                  haveEmail={haveEmail}
                />
                <button
                  className="btn btn-sm btn-outline-primary"
                  title="Download Invoice"
                  onClick={() => handleDownloadFinance(rowData)}
                >
                  <FiDownload />
                </button>
                </div>
            )}
          </Cell>
        </Column>
      </Table>

      {data && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={filteredTableData.length}
            pages={
              totalCount
                ? Math.ceil(totalCount / pageSize)
                : Math.ceil(filteredTableData.length / pageSize)
            }
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) =>
              dispatch(ActionsFinanceCaregiverTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
};
